import { useState, useRef, useEffect, memo } from "react";
import { MapPin, Search, Loader2, X, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import toast from "react-hot-toast";
import { loadGoogleMaps } from "@/lib/googleMapsLoader";

// Declaração global do Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface AddressSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

interface LocationPickerProps {
  initialCoordinates: [number, number] | null;
  onLocationChange: (location: {
    coordinates: [number, number];
    address: string;
    fullAddress: any;
  }) => void;
  height?: string;
  showOriginalMarker?: boolean;
}

export const LocationPicker = memo(({
  initialCoordinates,
  onLocationChange,
  height = "300px",
  showOriginalMarker = false,
}: LocationPickerProps) => {
  const [coordinates, setCoordinates] = useState<[number, number] | null>(
    initialCoordinates,
  );
  const [newCoordinates, setNewCoordinates] = useState<[number, number] | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasError, setHasError] = useState(false);
  const mapRef = useRef<any>(null);
  const originalMarkerRef = useRef<any>(null);
  const newMarkerRef = useRef<any>(null);
  const geocoder = useRef<any>(null);
  const searchTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const initMap = () => {
      try {
        if (!initialCoordinates) return;

        const mapElement = document.getElementById("location-picker-map");
        if (!mapElement || mapRef.current) return; // Não recriar se já existe

        const coords = coordinates || initialCoordinates;

        // Validar coordenadas
        if (
          !coords ||
          coords.length !== 2 ||
          typeof coords[0] !== "number" ||
          typeof coords[1] !== "number" ||
          isNaN(coords[0]) ||
          isNaN(coords[1])
        ) {
          console.error("Coordenadas inválidas:", coords);
          setHasError(true);
          return;
        }

        // Verificar se o Google Maps está disponível
        if (!window.google || !window.google.maps) {
          console.error("Google Maps não está disponível");
          setHasError(true);
          return;
        }

        // Criar mapa
        const map = new window.google.maps.Map(mapElement, {
          center: { lat: coords[0], lng: coords[1] },
          zoom: 16,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          mapId: "LOCATION_PICKER_MAP", // Necessário para AdvancedMarkerElement
        });

        if (showOriginalMarker) {
          // Criar marcador original (vermelho, fixo)
          const pinBackground = new window.google.maps.marker.PinElement({
            background: "#DC2626",
            borderColor: "#991B1B",
            glyphColor: "#FFFFFF",
          });

          const originalMarker =
            new window.google.maps.marker.AdvancedMarkerElement({
              position: { lat: coords[0], lng: coords[1] },
              map: map,
              content: pinBackground.element,
              title: "Localização Original",
            });

          originalMarkerRef.current = originalMarker;
        }

        // Evento de clique no mapa para criar novo marcador
        map.addListener("click", (e: any) => {
          if (e.latLng) {
            const newCoords: [number, number] = [
              e.latLng.lat(),
              e.latLng.lng(),
            ];

            // Criar ou mover marcador verde
            if (!newMarkerRef.current) {
              const pinBackground = new window.google.maps.marker.PinElement({
                background: "#16A34A",
                borderColor: "#15803D",
                glyphColor: "#FFFFFF",
              });

              const newMarker =
                new window.google.maps.marker.AdvancedMarkerElement({
                  position: e.latLng,
                  map: map,
                  content: pinBackground.element,
                  title: "Nova Localização",
                  gmpDraggable: true,
                });

              newMarker.addListener("dragend", () => {
                const position = newMarker.position as any;
                if (position) {
                  const draggedCoords: [number, number] = [
                    position.lat,
                    position.lng,
                  ];
                  setNewCoordinates(draggedCoords);
                  reverseGeocode(draggedCoords);
                }
              });

              newMarkerRef.current = newMarker;
            } else {
              newMarkerRef.current.position = e.latLng;
            }

            setNewCoordinates(newCoords);
            reverseGeocode(newCoords);
          }
        });

        mapRef.current = map;
        geocoder.current = new window.google.maps.Geocoder();
      } catch (error) {
        console.error("Erro ao inicializar LocationPicker:", error);
        setHasError(true);
      }
    };

    // Carregar Google Maps usando loader global
    loadGoogleMaps(initMap);
  }, []); // Inicializar apenas uma vez

  useEffect(() => {
    if (
      mapRef.current &&
      newCoordinates &&
      newCoordinates.length === 2 &&
      typeof newCoordinates[0] === "number" &&
      typeof newCoordinates[1] === "number" &&
      !isNaN(newCoordinates[0]) &&
      !isNaN(newCoordinates[1])
    ) {
      const position = { lat: newCoordinates[0], lng: newCoordinates[1] };
      mapRef.current.setCenter(position);
    }
  }, [newCoordinates]);

  const reverseGeocode = async (coords: [number, number]) => {
    if (!geocoder.current) return;

    try {
      const response = await geocoder.current.geocode({
        location: { lat: coords[0], lng: coords[1] },
      });

      if (response.results && response.results[0]) {
        const result = response.results[0];
        const addressComponents = result.address_components;

        // Extrair componentes do endereço
        const getComponent = (type: string) => {
          const component = addressComponents.find((c: any) =>
            c.types.includes(type),
          );
          return component?.long_name || "";
        };

        const route = getComponent("route");
        const streetNumber = getComponent("street_number");
        const sublocality =
          getComponent("sublocality_level_1") || getComponent("sublocality");
        const locality =
          getComponent("locality") ||
          getComponent("administrative_area_level_2");
        const state = getComponent("administrative_area_level_1");
        const postalCode = getComponent("postal_code");
        const country = getComponent("country");

        // Formatar endereço completo: R. Joaquim Pereira, 30 - Frecheirinha, CE, 62340-000, Brasil
        const addressParts = [];

        // Parte 1: Rua e número
        if (route) {
          const roadPart = streetNumber ? `${route}, ${streetNumber}` : route;
          addressParts.push(roadPart);
        }

        // Parte 2: Bairro, Cidade, Estado
        const locationParts = [];
        if (sublocality) locationParts.push(sublocality);
        if (locality) locationParts.push(locality);
        if (state) locationParts.push(state);

        if (locationParts.length > 0) {
          addressParts.push(locationParts.join(", "));
        }

        // Parte 3: CEP
        if (postalCode) {
          addressParts.push(postalCode);
        }

        // Parte 4: País
        if (country) {
          addressParts.push(country);
        }

        const formattedAddress =
          addressParts.length > 0
            ? addressParts.join(", ")
            : result.formatted_address;

        onLocationChange({
          coordinates: coords,
          address: formattedAddress,
          fullAddress: {
            route,
            streetNumber,
            sublocality,
            locality,
            state,
            postalCode,
            country,
            formatted: result.formatted_address,
          },
        });
      }
    } catch (error) {
      console.error("Erro ao buscar endereço:", error);
      toast.error("Erro ao buscar informações do endereço");
    }
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);

    try {
      // Usar a nova API AutocompleteSuggestion
      const request = {
        input: query,
        includedRegionCodes: ["br"],
      };

      const { suggestions: autocompleteSuggestions } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request,
        );

      if (autocompleteSuggestions && autocompleteSuggestions.length > 0) {
        const formattedSuggestions = autocompleteSuggestions.map(
          (suggestion: any) => {
            const placePrediction = suggestion.placePrediction;
            return {
              placeId: placePrediction?.placeId || "",
              description: placePrediction?.text?.text || "",
              mainText:
                placePrediction?.mainText?.text ||
                placePrediction?.text?.text ||
                "",
              secondaryText: placePrediction?.secondaryText?.text || "",
            };
          },
        );
        setSuggestions(formattedSuggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error("Erro ao buscar endereço:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce da pesquisa
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 500);
  };

  const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
    if (!suggestion.placeId) return;

    try {
      // Usar nova API Place.fetchFields
      const place = new window.google.maps.places.Place({
        id: suggestion.placeId,
      });

      await place.fetchFields({
        fields: ["location", "addressComponents", "formattedAddress"],
      });

      const location = place.location;
      if (!location) return;

      const newCoords: [number, number] = [location.lat(), location.lng()];

      // Extrair componentes do endereço
      const addressComponents = place.addressComponents || [];
      const getComponent = (type: string) => {
        const component = addressComponents.find((c: any) =>
          c.types.includes(type),
        );
        return component?.longText || "";
      };

      const route = getComponent("route");
      const streetNumber = getComponent("street_number");
      const sublocality =
        getComponent("sublocality_level_1") || getComponent("sublocality");
      const locality =
        getComponent("locality") || getComponent("administrative_area_level_2");
      const state = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");

      // Formatar endereço completo
      const addressParts = [];

      if (route) {
        const roadPart = streetNumber ? `${route}, ${streetNumber}` : route;
        addressParts.push(roadPart);
      }

      const locationParts = [];
      if (sublocality) locationParts.push(sublocality);
      if (locality) locationParts.push(locality);
      if (state) locationParts.push(state);

      if (locationParts.length > 0) {
        addressParts.push(locationParts.join(", "));
      }

      if (postalCode) {
        addressParts.push(postalCode);
      }

      if (country) {
        addressParts.push(country);
      }

      const formattedAddress =
        addressParts.length > 0
          ? addressParts.join(", ")
          : place.formattedAddress || suggestion.description;

      setNewCoordinates(newCoords);
      setSearchQuery(formattedAddress);
      setShowSuggestions(false);

      // Criar ou mover marcador verde ao selecionar sugestão
      if (mapRef.current) {
        if (!newMarkerRef.current) {
          const pinBackground = new window.google.maps.marker.PinElement({
            background: "#16A34A",
            borderColor: "#15803D",
            glyphColor: "#FFFFFF",
          });

          const newMarker = new window.google.maps.marker.AdvancedMarkerElement(
            {
              position: location,
              map: mapRef.current,
              content: pinBackground.element,
              title: "Nova Localização",
              gmpDraggable: true,
            },
          );

          newMarker.addListener("dragend", () => {
            const position = newMarker.position as any;
            if (position) {
              const draggedCoords: [number, number] = [
                position.lat,
                position.lng,
              ];
              setNewCoordinates(draggedCoords);
              reverseGeocode(draggedCoords);
            }
          });

          newMarkerRef.current = newMarker;
        } else {
          newMarkerRef.current.position = location;
        }

        mapRef.current.setCenter(location);
        mapRef.current.setZoom(16);
      }

      onLocationChange({
        coordinates: newCoords,
        address: formattedAddress,
        fullAddress: {
          route,
          streetNumber,
          sublocality,
          locality,
          state,
          postalCode,
          country,
          formatted: place.formattedAddress,
        },
      });
    } catch (error) {
      console.error("Erro ao buscar detalhes do local:", error);
      toast.error("Erro ao buscar detalhes do endereço");
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-3">
      {/* Input de Pesquisa */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Pesquisar endereço..."
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>

        {/* Sugestões */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-[1100] w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b last:border-b-0"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {suggestion.mainText}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {suggestion.secondaryText}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Instruções */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          <strong>Como usar:</strong> Clique no mapa para selecionar uma nova
          localização ou arraste o marcador. Você também pode pesquisar um
          endereço no campo acima.
        </p>
      </div>

      {/* Mapa ou mensagem de erro */}
      {hasError ? (
        <div
          style={{ height, width: "100%" }}
          className="rounded-lg overflow-hidden border flex items-center justify-center bg-gray-100"
        >
          <div className="text-center p-4">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700 mb-1">
              Não foi possível carregar o seletor de localização
            </p>
            <p className="text-xs text-gray-500">
              Verifique sua conexão ou tente recarregar a página
            </p>
          </div>
        </div>
      ) : (
        <div
          id="location-picker-map"
          style={{ height, width: "100%" }}
          className="rounded-lg overflow-hidden border"
        />
      )}
    </div>
  );
});
